import GameModel from "../../GameModel";
import { Settings } from "../../Settings";
import Totals from "../crew/Totals";
import BigNum from "../math/BigNum";
import Vector from "../math/Vector";
import SkillNodePurchaseListener from "./SkillNodePurchaseListener";

export default class SkillNode extends GameModel {
    /**
     * @param {string} id 
     * @param {string} title 
     * @param {string} description 
     * @param {Totals} totals
     * @param {string} iconFileName 
     * @param {BigNum} cost 
     * @param {SkillNodePurchaseListener} purchaseListener
     */
    constructor(id, title, description, totals, iconFileName, cost, purchaseListener) {
        super();

        /**
         * @type {SkillNode[]}
         */
        this.parents = null;
        /**
         * @type {SkillNode[]}
         */
        this.children = null;

        /**
         * @constant
         * @type {string}
         */
        this.id = id;
        /**
         * @constant
         * @type {Totals}
         */
        this.totals = totals;

        /**
         * @constant
         * @type {string}
         */
        this.title = title;
        /**
         * @constant
         * @type {string}
         */
        this.description = description;
        /**
         * @constant
         * @type {string}
         */
        this.iconFileName = iconFileName;
        /**
         * @constant
         * @type {BigNum}
         */
        this.cost = cost;
        /**
         * The fixed position in the graph.
         * @constant
         * @type {Vector}
         */
        this.graphCoordinate = new Vector(0, 0);
        /**
         * The projected position on the UI, based on pan/zoom.
         * @constant
         * @type {Vector}
         */
        this.viewCoordinate = new Vector(0, 0);
        /**
         * @type {boolean}
         */
        this.nodeVisible = true;
        /**
         * @constant
         * @type {SkillNodePurchaseListener}
         */
        this.purchaseListener = purchaseListener;

        ////////////////////////////////////////////
        // Stateful properties
        ////////////////////////////////////////////

        /**
         * @type {boolean}
         */
        this.parentNodePurchased = false;

        /**
         * @type {boolean}
         */
        this.purchased = false;
    }

    /**
     * @returns {string}
     */
    getTitle() {
        return this.title;
    }

    /**
     * @returns {string}
     */
    getDescription() {
        return this.description;
    }

    /**
     * @param {number} cellX The x-coordinate in cells (not pixels)
     * @param {number} cellY The y-coordinate in cells (not pixels)
     */
    setPosition(cellX, cellY) {
        this.graphCoordinate.set(Settings.skillGraph.cellWidth * cellX, Settings.skillGraph.cellHeight * cellY);
    }

    /**
     * @returns {BigNum}
     */
    getCost() {
        return this.cost;
    }

    /**
     * This should only be called internally by the skill node.
     * @private
     * @param {SkillNode} node
     */
    addParentNode(node) {
        if (!this.parents) {
            this.parents = [];
        }
        this.parents.push(node);
    }

    /**
     * @param {SkillNode} node 
     */
    addChildNode(node) {
        if (!this.children) {
            this.children = [];
        }
        this.children.push(node);

        // This node is added as a parent to the child.
        node.addParentNode(this);
    }

    /**
     * @override
     * @returns {boolean}
     */
    isAffordable() {
        return this.cost.lessThanEquals(this.totals.prestige);
    }

    /**
     * @override
     * @returns {boolean}
     */
    isPurchasable() {
        if (this.purchased) {
            return false;
        }
        if (!this.isAffordable()) {
            return false;
        }

        // If this is a root node, then at this point it is purchaseable.
        if (!this.parents || this.parents.length === 0) {
            return true;
        }

        // The node is purchaseable if any of the parent nodes have been purchased.
        return this.parentNodePurchased;
    }

    /**
     * @returns {boolean}
     */
    onPurchase() {
        if (this.purchased) {
            console.log('SkillNode.onPurchase() node already purchased');
            return false;
        }
        if (!this.isAffordable()) {
            console.log('SkillNode.onPurchase() not affordable');
            return false;
        }

        this.purchased = true;

        this.totals.decrementPrestige(this.cost);

        // Let the listener know that this node has been purchased.
        this.purchaseListener.onNodePurchased(this.cost);

        // This node is purchased, so make sure all children have their parentNodePurcased value set to true.
        this.setChildrenParentNodePurchased();

        return true;
    }

    onPurchaseFromSave() {
        if (this.purchased) {
            console.log('SkillNode.onPurchaseFromSave() node already purchased');
            return false;
        }

        this.purchased = true;
        this.purchaseListener.onNodePurchasedFromSave();

        // Do not decrement the currency.

        // This node is purchased, so make sure all children have their parentNodePurcased value set to true.
        this.setChildrenParentNodePurchased();
    }

    onSaveLoad() {
        if (this.purchased) {
            this.setChildrenParentNodePurchased();
        }
    }

    /**
     * @private
     */
    setChildrenParentNodePurchased() {
        if (this.children) {
            for (let i = 0; i < this.children.length; i++) {
                this.children[i].parentNodePurchased = true;
            }
        }
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.parentNodePurchased = false;
        this.purchased = false;
    }

    /**
     * @override
     */
    resetForPrestige() {
        // skills are not reset on prestige
    }

}